import { useSelector } from "react-redux";

import {
  FeatureGates,
  FeatureGateValue,
  getFeatureGates,
  getFeatureGateValueFromFeatureGates,
  getLocaleWithScript,
  RouteConstraint,
} from "@gamedev-web/gamedev-sdk-core";

import { hasKey } from "../helpers";

const evaluateConstraint = (
  constraint: RouteConstraint,
  getFeatureGateValue: (featureGate: string) => FeatureGateValue,
  locale: string | undefined
): boolean => {
  if (Array.isArray(constraint)) {
    // If it's an array of constraints, we just need one of them to return true satisfy the main constraint
    for (const innerConstraint of constraint) {
      const result = evaluateConstraint(innerConstraint, getFeatureGateValue, locale);
      if (result) {
        return true;
      }
    }
    return false;
  } else {
    if (hasKey(constraint, "featureGates")) {
      for (const [featureGate, validateFeatureGateValue] of Object.entries(
        constraint.featureGates
      )) {
        if (!validateFeatureGateValue(getFeatureGateValue(featureGate))) {
          return false;
        }
      }
    }

    if (
      hasKey(constraint, "locale") &&
      constraint.locale.toLocaleLowerCase() !== locale?.toLocaleLowerCase()
    ) {
      return false;
    }

    return true;
  }
};

/**
 * Evaluates a given route constraint that decides whether a route is enabled or not
 * @param constraint The route constraint
 * @param featureGates The feature gates, mapped from treatments and overrides
 * @returns A value indicating whether the route constraint evaluated to true/false
 */
export const evaluateRouteConstraint = (
  constraint?: RouteConstraint,
  featureGates?: FeatureGates,
  locale?: string
): boolean => {
  if (!constraint) {
    return true;
  }

  const getFeatureGateValue = (featureGate: string) =>
    getFeatureGateValueFromFeatureGates(featureGates ?? {}, featureGate);

  return evaluateConstraint(constraint, getFeatureGateValue, locale);
};

export const useEvaluateRouteConstraint = (constraint: RouteConstraint | undefined) => {
  const featureGates = useSelector(getFeatureGates);
  const locale = useSelector(getLocaleWithScript);
  return evaluateRouteConstraint(constraint, featureGates, locale);
};
